import { defineComponent as _defineComponent } from 'vue';
import { renderSlot as _renderSlot, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, normalizeClass as _normalizeClass, withModifiers as _withModifiers, toDisplayString as _toDisplayString, createBlock as _createBlock } from "vue";
const _hoisted_1 = { class: "d-flex flex-wrap justify-center ga-2" };
const _hoisted_2 = ["accept"];
const _hoisted_3 = {
    key: 1,
    class: "text-caption text-medium-emphasis"
};
const _hoisted_4 = {
    key: 0,
    class: "text-body-2 text-medium-emphasis mt-2 mb-0"
};
import { ref, watch } from "vue";
import { describeAccept, matchesAccept, parseAccept, usePastedFiles } from "@/composables/usePastedFiles";
import { CHART_ACCEPT } from "@/composables/useChartUpload";
/**
 * The dashed drop area: drag and drop, Choose files, and Ctrl+V or Paste screenshot. Rows go in the default slot.
 * `staged` is the parent's list of added files: a file that leaves it may be pasted again.
 */
export default /*@__PURE__*/ _defineComponent({
    __name: 'DocumentDropZone',
    props: {
        disabled: { type: Boolean, default: false },
        empty: { type: Boolean, default: false },
        staged: { default: () => [] }
    },
    emits: ["add-files"],
    setup(__props, { expose: __expose, emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const fileInput = ref(null);
        const dropDepth = ref(0);
        const dropAlert = ref(null);
        const addFiles = (files) => {
            if (files.length)
                emit("add-files", files);
        };
        const { pasteFromClipboard, pasting, awaitingPastePrompt, pasteAlert, forgetPaste } = usePastedFiles({
            onFiles: addFiles,
            accept: () => CHART_ACCEPT,
            listen: true,
            enabled: () => !props.disabled,
        });
        const onPick = (event) => {
            const input = event.target;
            const files = Array.from(input.files ?? []);
            input.value = "";
            addFiles(files);
        };
        const onDrop = (event) => {
            dropDepth.value = 0;
            dropAlert.value = null;
            if (props.disabled)
                return;
            const rules = parseAccept(CHART_ACCEPT);
            const files = Array.from(event.dataTransfer?.files ?? []);
            const accepted = files.filter((file) => matchesAccept(file, rules));
            const skipped = files.length - accepted.length;
            if (skipped) {
                dropAlert.value = `Skipped ${skipped} ${skipped === 1 ? "file" : "files"} of an unsupported type. Supported types: ${describeAccept(rules)}.`;
            }
            addFiles(accepted);
        };
        watch(() => [...props.staged], (next, previous) => previous.filter((item) => !next.includes(item) && item.file).forEach((item) => forgetPaste(item.file.name)));
        __expose({ pasting });
        return (_ctx, _cache) => {
            const _component_v_icon = _resolveComponent("v-icon");
            const _component_v_btn = _resolveComponent("v-btn");
            const _component_v_alert = _resolveComponent("v-alert");
            return (_openBlock(), _createElementBlock("div", null, [
                _createElementVNode("div", {
                    class: _normalizeClass(["drop-zone rounded-lg", { 'drop-zone--drop': dropDepth.value > 0 && !__props.disabled, 'drop-zone--empty': __props.empty }]),
                    onDragover: _cache[1] || (_cache[1] = _withModifiers(() => { }, ["prevent"])),
                    onDragenter: _cache[2] || (_cache[2] = _withModifiers(($event) => (dropDepth.value++), ["prevent"])),
                    onDragleave: _cache[3] || (_cache[3] = _withModifiers(($event) => (dropDepth.value = Math.max(0, dropDepth.value - 1)), ["prevent"])),
                    onDrop: _withModifiers(onDrop, ["prevent"])
                }, [
                    _renderSlot(_ctx.$slots, "default"),
                    _createElementVNode("div", {
                        class: _normalizeClass(["drop-zone__add d-flex align-center ga-2", __props.empty ? 'flex-column pa-6' : 'flex-wrap px-3 py-2'])
                    }, [
                        (__props.empty)
                            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                _createVNode(_component_v_icon, {
                                    size: "28",
                                    color: "primary"
                                }, {
                                    default: _withCtx(() => [...(_cache[4] || (_cache[4] = [
                                            _createTextVNode("mdi-tray-arrow-up", -1)
                                        ]))]),
                                    _: 1
                                }),
                                _cache[5] || (_cache[5] = _createElementVNode("span", { class: "text-body-2 d-none d-sm-inline" }, "Drag files here, or", -1))
                            ], 64))
                            : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_1, [
                            _createElementVNode("input", {
                                ref_key: "fileInput",
                                ref: fileInput,
                                type: "file",
                                multiple: "",
                                accept: _unref(CHART_ACCEPT),
                                class: "d-none",
                                onChange: onPick
                            }, null, 40, _hoisted_2),
                            _createVNode(_component_v_btn, {
                                variant: "tonal",
                                color: "primary",
                                size: "default",
                                "prepend-icon": "mdi-paperclip",
                                disabled: __props.disabled,
                                onClick: _cache[0] || (_cache[0] = ($event) => (fileInput.value?.click()))
                            }, {
                                default: _withCtx(() => [...(_cache[6] || (_cache[6] = [
                                        _createTextVNode(" Choose files ", -1)
                                    ]))]),
                                _: 1
                            }, 8, ["disabled"]),
                            _createVNode(_component_v_btn, {
                                variant: "text",
                                color: "primary",
                                size: "default",
                                "prepend-icon": "mdi-clipboard-outline",
                                loading: _unref(pasting),
                                disabled: __props.disabled,
                                onClick: _unref(pasteFromClipboard)
                            }, {
                                default: _withCtx(() => [...(_cache[7] || (_cache[7] = [
                                        _createTextVNode(" Paste screenshot ", -1)
                                    ]))]),
                                _: 1
                            }, 8, ["loading", "disabled", "onClick"])
                        ]),
                        (__props.empty)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_3, "PDF, PNG, JPG or TIFF"))
                            : _createCommentVNode("", true)
                    ], 2)
                ], 34),
                (_unref(awaitingPastePrompt))
                    ? (_openBlock(), _createElementBlock("p", _hoisted_4, [...(_cache[8] || (_cache[8] = [
                            _createTextVNode(" Waiting for the browser. Click ", -1),
                            _createElementVNode("strong", null, "Paste", -1),
                            _createTextVNode(" in the prompt it just opened. ", -1)
                        ]))]))
                    : _createCommentVNode("", true),
                (_unref(pasteAlert))
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 1,
                        type: _unref(pasteAlert).type,
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(pasteAlert).text), 1)
                        ]),
                        _: 1
                    }, 8, ["type"]))
                    : _createCommentVNode("", true),
                (dropAlert.value)
                    ? (_openBlock(), _createBlock(_component_v_alert, {
                        key: 2,
                        type: "warning",
                        variant: "tonal",
                        density: "compact",
                        class: "mt-2"
                    }, {
                        default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(dropAlert.value), 1)
                        ]),
                        _: 1
                    }))
                    : _createCommentVNode("", true)
            ]));
        };
    }
});
